import { ReactElement } from 'react';

import CTA from 'components/UI/Cta';
import RichText from 'components/UI/Richtext';
import type { Document } from '@contentful/rich-text-types';
import type { IFragmentAnchorLinkFields } from 'types/contentful';

const ANCHOR_CTA_SCHEME = 'OutlinedTeal' as const;

function targetElementIdFromCmsLink(raw: string): string {
  const s = raw.trim();
  const hashIdx = s.lastIndexOf('#');
  if (hashIdx !== -1) {
    return s.slice(hashIdx + 1).replace(/^[/\s]+/, '');
  }
  return s.replace(/^[#/]+/, '');
}

function scrollToAnchor(rawLink: string): void {
  const sectionId = targetElementIdFromCmsLink(rawLink);
  if (!sectionId) return;

  const el = document.getElementById(sectionId);
  if (!el) return;

  const header = document.querySelector('header');
  const headerHeight = header ? (header as HTMLElement).offsetHeight : 0;
  const elementTop = el.getBoundingClientRect().top + window.scrollY;
  window.scrollTo({
    top: elementTop - headerHeight,
    behavior: 'smooth',
  });
}

export interface AnchorLinksProps {
  id: string;
  heading: Document;
  copy?: Document | undefined;
  links: IFragmentAnchorLinkFields[];
  className?: string;
}

export default function AnchorLinks({
  id,
  heading,
  copy,
  links,
  className,
}: AnchorLinksProps): ReactElement {
  return (
    <div
      className={`px-site-component pb-10 md:pb-20 text-primary-actual ${className || ''}`}
      id={id}
    >
      <div className="space-y-6 md:space-y-8">
        <div className="relative text-center">
          {heading && (
            <RichText
              document={heading}
              classNames="font-heading text-new-34 lg:text-4xl lg:leading-new-43 mb-0 text-primary-actual richtext-font-inherit richtext-nomargin-headings iframe-richtext styles_richtext_anchor__1ZSI3 styles_richtext_bullet_points__3bOrw styles_richtext_ordered_lists__3IdxI styles_richtext_blockquote__2WGVu styles_richtext_ruler__VLE_h styles_richtext_header__32pqU styles_center_richtext_content__3VGED"
              noEmpty={true}
            />
          )}
        </div>
        {copy && (
          <RichText
            document={copy}
            classNames="text-lg text-primary-actual font-light text-center richtext-font-inherit center_richtext_content styles_richtext_anchor__1ZSI3 styles_richtext_bullet_points__3bOrw styles_richtext_ordered_lists__3IdxI styles_richtext_blockquote__2WGVu styles_richtext_ruler__VLE_h styles_richtext_header__32pqU styles_center_richtext_content__3VGED"
          />
        )}

        {links.length > 0 && (
          <div className="flex flex-row items-center justify-center flex-wrap md:items-center gap-2">
            {links.map((item) => {
              const slug = `#${targetElementIdFromCmsLink(item.link)}`;
              return (
                <div key={item.id}>
                  <CTA
                    text={item.linkText}
                    slug={slug}
                    colorScheme={ANCHOR_CTA_SCHEME}
                    classNames="text-primary-actual button-text bg-white"
                    onClick={() => scrollToAnchor(item.link)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
